import * as React from "react";
import { Store } from "redux";
import type { Dispatch } from "redux";
import { connect } from "react-redux";
import { createReduxStore } from "../redux/reduxHelpers";

import { translate } from "../IntlProviderWrapper";
import { FormattedMessage } from "react-intl";
import Toggle from "react-toggle";
import { getBackend as getBackend2 } from "../../lib/backend/Backend2";
import * as XLSX from "xlsx";
import * as Auth from "../../lib/Auth";
import local_settings, { FIELD_LAST_LINEGROUP, FIELD_LAST_PLANT } from "../../lib/LocalSettings";
import { USER_TAGS } from "../../lib/CommonTags.generated";
import {
    SET_INBOX_VIEW,
    SET_TICKETS_VIEW,
    TICKETING_VIEWS,
    SET_WORKFLOW,
    SET_WORKFLOWS,
    SET_TICKETS_DATA,
    SET_INBOX_ITEM_VIEW,
    SET_INCLUDE_CLOSED,
} from "./reducers/ticketing";
import { reducers } from "./reducers/reducers";
import type { ActionTypes } from "./reducers/ticketing";
import type { ReduxState } from "./reducers/reducers";
import { classNames } from "../../lib/Util";

import img_refresh_white from "../../img/refresh-white.svg";

type OwnProps = {
    is_kiosk: boolean;
    children?: React.ReactNode;
};

type Props = OwnProps & ReduxProps;

class TicketingNavigation extends React.Component<Props> {
    store: Store;

    constructor(props: Props) {
        super(props);
        this.store = createReduxStore(reducers);
    }

    componentDidMount() {
        this.load();
    }
    componentDidUpdate(prev_props: Props) {
        if (prev_props.selected_workflow != this.props.selected_workflow) {
            this.load();
        }
    }
    async load() {
        if (this.props.workflows.length == 0) {
            const { workflows } = await getBackend2().ticketing.getWorkflows({});
            const data = workflows
                .map(x => ({ label: x.title, value: x.name }))
                .sort((a, b) => {
                    // Prioritize "tool_setup" by moving it to the front
                    if (a.value === "tool_setup") return -1;
                    if (b.value === "tool_setup") return 1;

                    // If neither is "tool_setup", sort alphabetically by label
                    return a.label.localeCompare(b.label);
                });

            this.props.reduxDispatch({ type: SET_WORKFLOWS, data });
            if (data.length > 0) {
                const val: string = data[0].value;
                this.props.reduxDispatch({ type: SET_WORKFLOW, data: val });
            }
        }
        this.refreshData();
    }

    goBack = () => {
        const prev_view = this.props.prev_view;
        if (prev_view) {
            this.props.reduxDispatch({ type: SET_TICKETS_VIEW, data: prev_view });
        }
    };

    goTickets = () => {
        this.props.reduxDispatch({ type: SET_TICKETS_VIEW, data: "tickets" });
    };

    goInbox = () => {
        this.props.reduxDispatch({ type: SET_INBOX_VIEW, data: undefined });
    };

    refreshData = async () => {
        const selected_workflow = this.props.selected_workflow;

        const res = await getBackend2().ticketing.searchTickets({
            include_closed: this.props.include_closed,
            include_errored: true,
            workflow_name: selected_workflow,
            linegroup_uuid: local_settings.getItem(FIELD_LAST_LINEGROUP) || "",
        });
        const tickets = res.hits.sort((a, b) => a.assigned_on - b.assigned_on);
        if (this.store !== null) {
            this.store.getState().ticketing.tickets = tickets;
        }
        this.props.reduxDispatch({ type: SET_TICKETS_DATA, data: tickets });
    };

    handleChangeDropdown = async (value: string) => {
        this.props.reduxDispatch({ type: SET_WORKFLOW, data: value });
        await this.refreshData();
    };

    async handleCheckboxChange() {
        const new_val: boolean = !this.props.include_closed;
        this.props.reduxDispatch({ type: SET_INCLUDE_CLOSED, data: new_val });
        await this.refreshData();
    }

    saveReport = async () => {
        const selected_workflow = this.props.selected_workflow;
        const { report } = await getBackend2().ticketing.downloadTicketingReport({
            workflow_name: selected_workflow,
            plant_uuid: local_settings.getItem(FIELD_LAST_PLANT) || "UNKNOWN",
        });
        let sheet_name = translate("common.save_report", "Save report");
        let file_name = "ticketing_report.xlsx";

        const wb = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(report);

        XLSX.utils.book_append_sheet(wb, worksheet, sheet_name);
        XLSX.writeFile(wb, file_name);
    };

    render() {
        const hide_back_on = [SET_TICKETS_VIEW, SET_INBOX_VIEW, SET_INBOX_ITEM_VIEW];
        const show_back = !hide_back_on.includes(this.props.selected_view);

        if (this.props.is_kiosk) {
            return null;
        }

        const is_kiosk = Auth.getUserTag(USER_TAGS.kiosk) === "true";
        const is_ticketing_admin = !is_kiosk && Auth.isInRole(Auth.ROLE_TICKET_ADMIN);

        return (
            <div className="navigation">
                <div className="navigation-header" style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        {show_back && (
                            <button className="btn btn-primary" onClick={this.goBack}>
                                {translate("common.back", "Back")}
                            </button>
                        )}
                        {this.props.selected_view == "tickets" && (
                            <select
                                className="form-control"
                                value={this.props.selected_workflow}
                                style={{ minWidth: "250px" }}
                                onChange={option => {
                                    this.handleChangeDropdown(option.target.value);
                                }}
                                // TOTEST
                            >
                                {this.props.workflows.map(m => {
                                    return (
                                        <option value={m.value} key={"key-option-" + m.value}>
                                            {m.label}
                                        </option>
                                    );
                                })}
                            </select>
                        )}
                        {this.props.selected_view == "tickets" && (
                            <div style={{ whiteSpace: "nowrap" }}>
                                <label
                                    style={{
                                        top: "5px",
                                        paddingLeft: "8px",
                                        paddingRight: "5px",
                                        marginBottom: "0",
                                        verticalAlign: "middle",
                                    }}
                                >
                                    <FormattedMessage id="Ticketing.include_closed" defaultMessage="Include closed" />
                                </label>
                                <Toggle
                                    style={{ verticalAlign: "middle" }}
                                    isSelected={this.props.include_closed}
                                    onChange={() => {
                                        this.handleCheckboxChange();
                                    }}
                                />
                            </div>
                        )}
                        {this.props.selected_view == "tickets" && (
                            <button
                                className="btn btn-primary"
                                onClick={() => this.refreshData()}
                                style={{ marginLeft: "15px", minWidth: "120px" }}
                            >
                                <div style={{ display: "flex" }}>
                                    <img
                                        src={img_refresh_white}
                                        style={{ width: "20px", height: "20px", paddingTop: "3px" }}
                                    />
                                    <div style={{ paddingTop: "2px", paddingLeft: "3px", paddingRight: "5px" }}>
                                        <FormattedMessage id="common.refresh" defaultMessage="Refresh" />
                                    </div>
                                </div>
                            </button>
                        )}
                    </div>
                    {this.props.selected_view !== SET_INBOX_ITEM_VIEW && !show_back && (
                        <div className="router-buttons">
                            {is_ticketing_admin && (
                                <button className="btn btn-primary" onClick={this.saveReport}>
                                    <FormattedMessage id="common.save_report" defaultMessage="Save report" />
                                </button>
                            )}
                            <button
                                className={classNames("btn btn-primary ml-1", {
                                    selected: this.props.selected_view === TICKETING_VIEWS.tickets,
                                })}
                                onClick={this.goTickets}
                            >
                                <FormattedMessage id="Ticketing.browse_tickets" defaultMessage="Browse tickets" />
                            </button>
                            <button
                                className={classNames("btn btn-primary ml-1", {
                                    selected: this.props.selected_view === TICKETING_VIEWS.inbox,
                                })}
                                onClick={this.goInbox}
                            >
                                <FormattedMessage id="Ticketing.inbox" defaultMessage="Inbox" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const selected_view = state.ticketing.selected_view;
    const prev_view = state.ticketing.prev_view;
    const workflows = state.ticketing.workflows;
    const selected_workflow = state.ticketing.selected_workflow || "tool_setup";
    const include_closed = state.ticketing.include_closed;

    return { include_closed, selected_view, prev_view, workflows, selected_workflow };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(TicketingNavigation);
